import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Sparkles, ArrowRight, Trophy } from "lucide-react";
import { ShareButtons } from "@/components/game/share";
import { GameOffer } from "@/components/game/GameOffer";
import { sfx, SoundToggle } from "@/components/game/sound";
import { useLang } from "@/i18n";
import { Seo } from "@/components/Seo";
import { Board } from "@/components/game/Board";
import { Palette } from "@/components/game/Palette";
import { SaveToGallery } from "@/components/game/SaveToGallery";
import { FIGURES, SERIES, canPlace, matches } from "@/game/shapes";
import { pageJsonld, PAGES } from "@/seo.mjs";

let nextId = 1;

export default function Tablero() {
  const { t } = useLang();
  const g = t.game;
  const svgRef = useRef(null);
  const [pieces, setPieces] = useState([]);
  const [shape, setShape] = useState("tira4");
  const [rot, setRot] = useState(0);
  const [color, setColor] = useState("rosa");
  const [erase, setErase] = useState(false);
  const [hover, setHover] = useState(null);
  const [serie, setSerie] = useState(null);
  const [figKey, setFigKey] = useState(null);
  const [hint, setHint] = useState(false);

  const figure = FIGURES.find((f) => f.key === figKey);
  const done = figure ? matches(pieces, figure) : false;
  useEffect(() => { if (done) sfx.success(); }, [done]);
  const ghost = hover && !erase ? { shape, rot, color, x: hover[0], y: hover[1], id: "ghost" } : null;
  const ghostValid = ghost ? canPlace(pieces, ghost) : false;
  const figuresIn = serie ? FIGURES.filter((f) => f.serie === serie) : [];

  useEffect(() => {
    const onKey = (e) => { if (e.key === "r" || e.key === "R") setRot((r) => (r + 1) % 4); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const place = (x, y) => {
    if (erase) return;
    const p = { shape, rot, color, x, y, id: nextId++ };
    if (canPlace(pieces, p)) { setPieces([...pieces, p]); sfx.place(); } else sfx.wrong();
  };
  const remove = (id, e) => { if (erase) { e.stopPropagation(); setPieces(pieces.filter((p) => p.id !== id)); sfx.remove(); } };
  const pickSerie = (s) => { setSerie(s); setFigKey(s ? FIGURES.find((f) => f.serie === s)?.key : null); setPieces([]); setHint(false); };
  const pickFigure = (k) => { setFigKey(k); setPieces([]); setHint(false); };
  const caption = figure ? `${g.copyThis}: ${figure.name}` : g.free;

  const toolBtn = (testid, onClick, active, children, extra = "") => (
    <button key={testid} data-testid={testid} onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs md:text-sm font-semibold transition-colors duration-200 ${
        active ? "bg-[#2E5A3B] text-white" : "bg-white text-[#132A1D] border border-black/10 hover:bg-[#2E5A3B]/10"} ${extra}`}>
      {children}
    </button>
  );

  return (
    <div data-testid="game-page" className="pt-16 min-h-screen bg-[#E9F4FA]">
      <Seo data={{ path: "/tablero", title: g.seoTitle, description: g.seoDesc, image: PAGES.tablero.image, jsonld: pageJsonld("tablero") }} />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#C86A4B] font-bold mb-3">{g.kicker}</p>
            <h1 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#132A1D] tracking-tight">{g.title}</h1>
            <p className="text-[#5C5C5C] mt-4 max-w-xl leading-relaxed">{g.sub}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/retos" data-testid="game-retos-link" className="inline-flex items-center gap-2 rounded-full bg-[#2E5A3B] text-white px-6 py-3 text-sm font-semibold hover:bg-[#C86A4B] transition-colors duration-300"><Trophy size={16} /> {g.retos}</Link>
            <a href="/juegos/tablero/" data-testid="game-cards-page-link" className="inline-flex items-center gap-2 rounded-full border border-black/15 text-[#132A1D] px-6 py-3 text-sm font-semibold hover:bg-white transition-colors duration-300">{g.cards}</a>
            <Link to="/catalogo/tablero-pame3d" data-testid="game-buy-link" className="inline-flex items-center gap-2 rounded-full bg-[#C86A4B] text-white px-6 py-3 text-sm font-semibold hover:bg-[#2E5A3B] transition-colors duration-300">{g.buy} <ArrowRight size={16} /></Link>
          </div>
        </div>

        <GameOffer slug="tablero-pame3d" playPath="/tablero" />

        <div className="flex flex-wrap gap-2 mb-3" data-testid="game-modes">
          {toolBtn("mode-free", () => pickSerie(null), !serie, g.free)}
          {SERIES.map((s) => toolBtn(`serie-${s.normalize("NFD").replace(/[^a-z0-9]+/gi, "").toLowerCase()}`, () => pickSerie(s), serie === s, s))}
        </div>
        {serie && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="game-figures">
            {figuresIn.map((f) => toolBtn(`mode-${f.key}`, () => pickFigure(f.key), figKey === f.key, f.name,
              figKey === f.key ? "!bg-[#C86A4B]" : "!bg-[#BCE3F2]/60 !border-transparent"))}
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-8 items-start mt-5">
          <div className="lg:col-span-7 relative">
            <Board svgRef={svgRef} pieces={pieces} ghost={ghost} ghostValid={ghostValid} hint={hint && figure ? figure.pieces : null}
              onCell={place} onPiece={remove} onHover={setHover} className="w-full rounded-2xl shadow-[0_10px_40px_-15px_rgba(35,65,78,.35)]" />
            {done && (
              <div data-testid="game-success" className="absolute inset-x-4 top-4 bg-[#2E5A3B] text-white rounded-2xl px-6 py-4 flex items-center gap-3 shadow-lg animate-in fade-in">
                <Sparkles size={22} className="text-[#F2AEC4]" />
                <div><p className="font-serif-d text-2xl font-semibold leading-tight">{g.success}</p><p className="text-sm text-white/80">{g.successSub}</p></div>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
              <p className="text-xs text-[#5C5C5C]" data-testid="game-piece-count">{pieces.length} {g.pieces} · {g.tip}</p>
              <div className="flex flex-wrap items-center gap-2"><SoundToggle /><SaveToGallery svgRef={svgRef} caption={caption} game="tablero" disabled={!pieces.length} /><ShareButtons svgRef={svgRef} caption={caption} file="mi-figura-pame3d.png" url="https://pame3d.com/tablero" g={g} /></div>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-6">
            {figure && (
              <div className="bg-white rounded-2xl p-4 border border-black/5" data-testid="game-target">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="font-serif-d text-xl font-semibold text-[#132A1D]" data-testid="game-target-name">{g.copyThis}: {figure.name}</p>
                  {toolBtn("game-hint", () => setHint(!hint), hint, <>{hint ? <EyeOff size={14} /> : <Eye size={14} />} {g.hint}</>)}
                </div>
                <div className="flex gap-4 items-start">
                  <Board pieces={figure.pieces} interactive={false} className="w-40 md:w-48 rounded-xl shrink-0" />
                  <p className="text-xs text-[#5C5C5C] leading-relaxed">{figure.pieces.length} {g.piecesNeeded}{figure.meta ? ` · ${figure.meta}` : ""}</p>
                </div>
              </div>
            )}
            <Palette shape={shape} rot={rot} color={color} erase={erase} g={g}
              onShape={(k) => { setShape(k); setErase(false); }} onRotate={() => setRot((rot + 1) % 4)} onColor={() => setColor(color === "rosa" ? "oro" : "rosa")}
              onErase={() => setErase(!erase)} onUndo={() => { if (pieces.length) { setPieces(pieces.slice(0, -1)); sfx.remove(); } }} onClear={() => setPieces([])} />
          </div>
        </div>
      </div>
    </div>
  );
}
